import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  IconBrandFacebook,
  IconBrandInstagram,
  IconBrandTiktok,
  IconBrandYoutube,
  IconLink,
  IconX,
} from "@tabler/icons-react";

import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DashboardCard } from "@/components/dashboard/dashboard-card";
import { DialogNotice } from "@/components/common/ActionDialog";
import { UserAvatar } from "@/components/common/UserAvatar";
import { ImageUploader } from "@/components/ImageUploader/ImageUploader";
import { ImageCropModal } from "@/components/ImageCropModal/ImageCropModal";
import { RichTextEditor } from "@/components/Editor/RichTextEditor";
import { Field, SettingsGrid, SettingsSection, UnsavedBar, useSections } from "@/components/settings/settings-kit";
import { useAffiliate } from "@/contexts/affiliate/hooks";
import { useDriver } from "@/contexts/driver";
import { socialMediaService, type SocialMedia } from "@/services/social-media.service";
import { nicheService } from "@/services/niche.service";
import { storageService } from "@/services/storage.service";
import type { Niche } from "@/types/niche";
import { readFile } from "@/utils/canvasUtils";
import { formatstorageUrl } from "@/utils/formatters";
import { getRichTextLimitError } from "@/utils/richText";
import { formatSocialMediaUrl, validateSocialMediaInput } from "@/utils/socialMediaUtils";
import { cn } from "@/lib/utils";

const MAX_NICHES = 5;
const BIO_LIMIT = 4000;
const PLATFORMS = ["INSTAGRAM", "TIKTOK", "YOUTUBE", "FACEBOOK", "OTHER"] as const;

const PLATFORM_ICON: Record<string, typeof IconLink> = {
  INSTAGRAM: IconBrandInstagram,
  TIKTOK: IconBrandTiktok,
  YOUTUBE: IconBrandYoutube,
  FACEBOOK: IconBrandFacebook,
  OTHER: IconLink,
};

const PLATFORM_LABEL: Record<string, string> = {
  INSTAGRAM: "Instagram",
  TIKTOK: "TikTok",
  YOUTUBE: "YouTube",
  FACEBOOK: "Facebook",
  OTHER: "Website",
};

interface Form {
  username: string;
  bio: string;
  profileImage: string;
  nicheIds: string[];
  socialLinks: { id: string; username: string }[];
}

/** Bio is stored as HTML; the preview shows the words only. */
function plainText(html: string) {
  return html
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/\s+/g, " ")
    .trim();
}

function sameLinks(a: Form["socialLinks"], b: Form["socialLinks"]) {
  const norm = (l: Form["socialLinks"]) =>
    l
      .filter((x) => x.username.trim())
      .map((x) => `${x.id}:${x.username.trim()}`)
      .sort()
      .join("|");
  return norm(a) === norm(b);
}

function ProfileSkeleton() {
  return (
    <SettingsGrid
      form={
        <>
          {[2, 1, 1, 1].map((fields, i) => (
            <div key={i} className="rounded-lg border border-border bg-card">
              <div className="px-5 pb-3 pt-4 animate-pulse">
                <div className="h-4 w-28 rounded bg-muted" />
              </div>
              {i === 0 && (
                <div className="space-y-5 border-t border-border px-5 py-5 animate-pulse">
                  {Array.from({ length: fields }).map((_, j) => (
                    <div key={j} className="space-y-2">
                      <div className="h-3.5 w-24 rounded bg-muted" />
                      <div className="h-9 w-full rounded-lg bg-muted" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </>
      }
      aside={
        <div className="rounded-lg border border-border bg-card">
          <div className="px-5 pb-3 pt-4 animate-pulse">
            <div className="h-4 w-32 rounded bg-muted" />
          </div>
          <div className="flex items-start gap-4 px-5 pb-5 animate-pulse">
            <div className="size-14 rounded-full bg-muted" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-36 rounded bg-muted" />
              <div className="h-3 w-24 rounded bg-muted" />
              <div className="h-3 w-full rounded bg-muted" />
            </div>
          </div>
        </div>
      }
    />
  );
}

/**
 * What brands see: photo, username, bio, niches and links. The aside is
 * the card a brand reads on its affiliate page, redrawn as the form changes.
 */
export function ProfileDetails() {
  const { details, isLoading, error, updateDetails } = useAffiliate();
  const { isTourOpen } = useDriver();
  const sections = useSections("identity", isTourOpen);
  const [saving, setSaving] = useState(false);
  const [platforms, setPlatforms] = useState<SocialMedia[]>([]);
  const [niches, setNiches] = useState<Niche[]>([]);
  const [form, setForm] = useState<Form>({ username: "", bio: "", profileImage: "", nicheIds: [], socialLinks: [] });
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [cropSrc, setCropSrc] = useState("");
  const [cropFile, setCropFile] = useState<File | null>(null);

  const fromDetails = (): Form => ({
    username: details?.username || "",
    bio: details?.bio || "",
    profileImage: details?.profileImage || "",
    nicheIds: details?.niches?.map((n) => n.nicheId || n.id) ?? [],
    socialLinks: details?.socialLinks ?? [],
  });

  useEffect(() => {
    if (details) setForm(fromDetails());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details]);

  useEffect(() => {
    (async () => {
      try {
        const [p, n] = await Promise.all([socialMediaService.findAll(), nicheService.getAvailableNiches()]);
        setPlatforms(p.filter((x, i, all) => all.findIndex((y) => y.name === x.name) === i));
        setNiches(n);
      } catch {
        toast.error("Could not load niches and platforms");
      }
    })();
  }, []);

  useEffect(() => {
    return () => {
      if (form.profileImage.startsWith("blob:")) URL.revokeObjectURL(form.profileImage);
    };
  }, [form.profileImage]);

  const hasChanges = useMemo(() => {
    if (!details) return false;
    const base = fromDetails();
    return (
      form.username !== base.username ||
      form.bio !== base.bio ||
      photoFile !== null ||
      [...form.nicheIds].sort().join() !== [...base.nicheIds].sort().join() ||
      !sameLinks(form.socialLinks, base.socialLinks)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, photoFile, details]);

  const set = <K extends keyof Form>(key: K, value: Form[K]) => setForm((prev) => ({ ...prev, [key]: value }));

  const pickPhoto = async (file: File) => {
    try {
      setCropSrc(await readFile(file));
      setCropFile(file);
    } catch {
      toast.error("Could not read that image");
    }
  };

  const cropDone = (file: File) => {
    if (form.profileImage.startsWith("blob:")) URL.revokeObjectURL(form.profileImage);
    setPhotoFile(file);
    set("profileImage", URL.createObjectURL(file));
    setCropSrc("");
    setCropFile(null);
  };

  const setLink = (platformId: string, username: string) => {
    const exists = form.socialLinks.some((l) => l.id === platformId);
    set(
      "socialLinks",
      exists
        ? form.socialLinks.map((l) => (l.id === platformId ? { ...l, username } : l))
        : [...form.socialLinks, { id: platformId, username }]
    );
  };

  const reset = () => {
    if (form.profileImage.startsWith("blob:")) URL.revokeObjectURL(form.profileImage);
    setForm(fromDetails());
    setPhotoFile(null);
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasChanges) return;
    for (const link of form.socialLinks) {
      const platform = platforms.find((p) => p.id === link.id);
      if (platform && link.username.trim()) {
        const v = validateSocialMediaInput(link.username.trim(), platform.name);
        if (!v.isValid) {
          toast.error(v.error || `Check your ${PLATFORM_LABEL[platform.name] ?? platform.name} link`);
          return;
        }
      }
    }
    const bioError = getRichTextLimitError(form.bio || "", BIO_LIMIT);
    if (bioError) {
      toast.error(bioError);
      return;
    }
    setSaving(true);
    try {
      const base = fromDetails();
      const profileImage = photoFile ? await storageService.uploadProfileImage(photoFile) : undefined;
      await updateDetails({
        ...(form.username !== base.username ? { username: form.username.trim() } : {}),
        ...(form.bio !== base.bio ? { bio: form.bio } : {}),
        ...(profileImage ? { profileImage } : {}),
        ...([...form.nicheIds].sort().join() !== [...base.nicheIds].sort().join() ? { niches: form.nicheIds } : {}),
        ...(!sameLinks(form.socialLinks, base.socialLinks)
          ? { socialLinks: form.socialLinks.filter((l) => l.username.trim()).map((l) => ({ id: l.id, username: l.username.trim() })) }
          : {}),
      });
      setPhotoFile(null);
      toast.success("Profile saved");
    } catch {
      toast.error("Could not save your profile");
    } finally {
      setSaving(false);
    }
  };

  if (isLoading || !details) return <ProfileSkeleton />;

  const name = [details.firstName, details.lastName].filter(Boolean).join(" ").trim() || "Your name";
  const selectedNiches = form.nicheIds.map((id) => niches.find((n) => n.id === id)).filter((n): n is Niche => Boolean(n));
  const addable = niches.filter((n) => !form.nicheIds.includes(n.id));
  const visiblePlatforms = PLATFORMS.map((name) => platforms.find((p) => p.name === name)).filter((p): p is SocialMedia => Boolean(p));
  const previewImage = form.profileImage.startsWith("blob:") ? form.profileImage : formatstorageUrl(form.profileImage || "") || null;
  const bio = plainText(form.bio);
  const liveLinks = form.socialLinks
    .map((l) => ({ ...l, platform: platforms.find((p) => p.id === l.id)?.name ?? "OTHER" }))
    .filter((l) => l.username.trim() && formatSocialMediaUrl(l.username.trim(), l.platform));

  return (
    <form onSubmit={save}>
      {error && (
        <div className="mb-4">
          <DialogNotice tone="error">{error}</DialogNotice>
        </div>
      )}
      <SettingsGrid
        form={
          <>
            <SettingsSection
              id="identity-section"
              title="Photo and username"
              open={sections.isOpen("identity")}
              onToggle={() => sections.toggle("identity")}
              bodyClassName="space-y-5"
            >
              <div id="SDF-profile-photo">
                <ImageUploader
                  id="profile-photo-upload"
                  image={form.profileImage}
                  onChange={pickPhoto}
                  showPreview={false}
                  helpText="Your photo, PNG, JPG or WebP, up to 3MB"
                />
              </div>
              <Field id="SDF-username" label="Username" htmlFor="username" help="Brands see this next to your name">
                <div className="relative">
                  <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-muted-foreground">@</span>
                  <Input
                    id="username"
                    value={form.username}
                    onChange={(e) => set("username", e.target.value.replace(/\s/g, ""))}
                    autoComplete="username"
                    className="pl-7"
                  />
                </div>
              </Field>
            </SettingsSection>

            <SettingsSection
              id="bio-section"
              title="Bio"
              open={sections.isOpen("bio")}
              onToggle={() => sections.toggle("bio")}
            >
              <Field id="SDF-bio" label="About you" help="A few lines on what you make and who follows you">
                <RichTextEditor content={form.bio} onChange={(v) => set("bio", v)} placeholder="I make short cooking videos for busy parents" maxLength={BIO_LIMIT} />
              </Field>
            </SettingsSection>

            <SettingsSection
              id="niches-section"
              title="Niches"
              open={sections.isOpen("niches")}
              onToggle={() => sections.toggle("niches")}
            >
              <Field
                id="SDF-niches"
                label="Your niches"
                htmlFor="add-niche"
                trailing={`${selectedNiches.length} of ${MAX_NICHES}`}
                help="Brands find affiliates by niche"
              >
                <div className="space-y-2">
                  {selectedNiches.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {selectedNiches.map((n) => (
                        <span key={n.id} className="inline-flex items-center gap-1 rounded-md border border-border py-0.5 pl-2 pr-1 text-xs text-foreground">
                          {n.name}
                          <button
                            type="button"
                            onClick={() => set("nicheIds", form.nicheIds.filter((id) => id !== n.id))}
                            aria-label={`Remove ${n.name}`}
                            className="inline-flex size-4 items-center justify-center rounded-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            <IconX size={12} stroke={2} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  {niches.length === 0 ? (
                    <div className="flex h-9 w-full items-center rounded-lg border border-input px-3 text-sm text-muted-foreground opacity-60">
                      Loading niches
                    </div>
                  ) : (
                    <Select
                      value=""
                      onValueChange={(id) => id && form.nicheIds.length < MAX_NICHES && set("nicheIds", [...form.nicheIds, id])}
                      disabled={form.nicheIds.length >= MAX_NICHES || addable.length === 0}
                    >
                      <SelectTrigger id="add-niche">
                        <SelectValue placeholder={form.nicheIds.length >= MAX_NICHES ? `Up to ${MAX_NICHES} niches` : "Add a niche"} />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        {addable.map((n) => (
                          <SelectItem key={n.id} value={n.id}>
                            {n.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </Field>
            </SettingsSection>

            <SettingsSection
              id="links-section"
              title="Links"
              open={sections.isOpen("links")}
              onToggle={() => sections.toggle("links")}
            >
              <div id="SDF-social-media-links" className="space-y-4">
                {visiblePlatforms.length === 0 ? (
                  <div className="space-y-4 animate-pulse">
                    {[0, 1, 2].map((i) => (
                      <div key={i} className="h-9 w-full rounded-lg bg-muted" />
                    ))}
                  </div>
                ) : (
                  visiblePlatforms.map((platform) => {
                    const Icon = PLATFORM_ICON[platform.name] ?? IconLink;
                    const value = form.socialLinks.find((l) => l.id === platform.id)?.username ?? "";
                    const fieldId = `link-${platform.name.toLowerCase()}`;
                    return (
                      <Field key={platform.id} label={PLATFORM_LABEL[platform.name] ?? platform.name} htmlFor={fieldId}>
                        <div className="relative">
                          <Icon size={16} stroke={1.75} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden />
                          <Input
                            id={fieldId}
                            value={value}
                            onChange={(e) => setLink(platform.id, e.target.value)}
                            placeholder={platform.name === "OTHER" ? "https://" : "Username or profile link"}
                            autoComplete="off"
                            className="pl-9"
                          />
                        </div>
                      </Field>
                    );
                  })
                )}
              </div>
            </SettingsSection>
          </>
        }
        aside={
          <>
            <DashboardCard title="How brands see you" bodyClassName="px-5 pb-5">
              <div className="flex items-start gap-4">
                <UserAvatar src={previewImage} name={name} className="size-14 text-lg" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[15px] font-semibold text-foreground">{name}</div>
                  <div className="truncate text-xs text-muted-foreground">
                    {form.username ? `@${form.username}` : "No username yet"}
                  </div>
                  {(selectedNiches.length > 0 || liveLinks.length > 0) && (
                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      {selectedNiches.length > 0 && <span>{selectedNiches.map((n) => n.name).join(", ")}</span>}
                      {liveLinks.length > 0 && (
                        <span className="inline-flex items-center gap-2">
                          {liveLinks.map((l) => {
                            const Icon = PLATFORM_ICON[l.platform] ?? IconLink;
                            return <Icon key={l.id} size={16} stroke={1.75} aria-label={PLATFORM_LABEL[l.platform] ?? l.platform} />;
                          })}
                        </span>
                      )}
                    </div>
                  )}
                  <p className={cn("mt-2 text-sm", bio ? "line-clamp-3 text-muted-foreground" : "text-muted-foreground/70")}>
                    {bio || "Your bio shows here"}
                  </p>
                </div>
              </div>
            </DashboardCard>
            <UnsavedBar visible={hasChanges} saving={saving} onCancel={reset} saveId="SDF-save-button" />
          </>
        }
      />

      {cropSrc && cropFile && (
        <ImageCropModal
          imageSrc={cropSrc}
          onClose={() => {
            setCropSrc("");
            setCropFile(null);
          }}
          onCropComplete={cropDone}
          originalFileName={cropFile.name}
          aspectRatio={1}
          cropShape="round"
          title="Crop your photo"
          description="Drag to position, pinch or scroll to zoom"
        />
      )}
    </form>
  );
}
